import moment from "moment/moment";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf";
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker?worker";

const init = () => {
  try {
    if (typeof window === "undefined" || !("Worker" in window)) {
      throw new Error("Web Workers not supported in this environment.");
    }

    window.pdfjsWorker = pdfjsWorker;
    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
  } catch (error) {
    console.log(`Unable to load pdfjs: ${error}`);
  }
};

init();

import i18n from "@/plugins/i18n";
import {
  cleanStringValue,
  downloadFile,
  getImage,
  getLocalisedValue,
  sanitizeStringValue,
  wait,
} from "@/app/core-module/util/coreHelper";
import {
  splineRegular,
  splineMedium,
  splineSemiBold,
  splineBold,
} from "@/app/core-module/util/spline-font";
import { EnvSubscriberHelper } from "@/app/env_subscriber_helper";

const generateFilename = (employeeProfile, index) => {
  try {
    const civilId = employeeProfile?.user?.civilIdOrPassport?.trim() ?? "";

    let name = employeeProfile?.user?.firstName?.trim() ?? "";
    const lastName = employeeProfile?.user?.lastName?.trim() ?? "";
    if (lastName?.length) name += ` ${lastName}`;

    const rigNo = employeeProfile.rigNumber?.trim() ?? "";

    const fileName = `profile_${civilId}_${name}_${rigNo}`.toUpperCase();
    return sanitizeStringValue(fileName);
  } catch (_) {
    return `profile_${
      employeeProfile?.user?.civilIdOrPassport?.trim() ?? index ?? "0"
    }_${Date.now()}`;
  }
};

const generateProfileCardPdf = ({
  employeeProfiles,
  subscriberInfo,
  download = false,
  debug = false,
  output,
}) => {
  try {
    if (!employeeProfiles?.length) return;

    //region pdf default variables and constants
    const pageHeight = 600;
    const pageWidth = 1050; //1.75
    const normalFontSize = 13;
    const mediumFontSize = 10;

    const gridOneWidth = 500;
    const gridTwoWidth = pageWidth - gridOneWidth;
    const gridOneStart = 5;
    const gridOneEnd = gridOneStart + gridOneWidth;
    const gridTwoStart = gridOneStart + gridOneWidth;
    const gridTwoCenter = Math.floor(gridTwoWidth / 2) + gridOneWidth;
    const gridTwoEnd = pageWidth - 20;

    let lineHeight = Math.floor(normalFontSize / 2);
    let currentY = lineHeight;
    let xCenter = Math.floor(pageWidth / 2);
    //endregion

    //region jspdf doc init
    const doc = new jsPDF({
      orientation: "l",
      format: [pageWidth, pageHeight],
      unit: "px",
      compress: true,
    });

    //region font setup
    doc.addFileToVFS("SplineSans-Regular-normal.ttf", splineRegular);
    doc.addFont(
      "SplineSans-Regular-normal.ttf",
      "SplineSans-Regular",
      "normal"
    );

    doc.addFileToVFS("SplineSans-Medium-normal.ttf", splineMedium);
    doc.addFont("SplineSans-Medium-normal.ttf", "SplineSans-Medium", "normal");

    doc.addFileToVFS("SplineSans-SemiBold-normal.ttf", splineSemiBold);
    doc.addFont(
      "SplineSans-SemiBold-normal.ttf",
      "SplineSans-SemiBold",
      "normal"
    );

    doc.addFileToVFS("SplineSans-Bold-bold.ttf", splineBold);
    doc.addFont("SplineSans-Bold-bold.ttf", "SplineSans-Bold", "bold");
    //endregion
    //endregion

    //region utility functions
    const fontType = (type, weight) => {
      if (type === "bold" || weight === 700) {
        doc.setFont("SplineSans-Bold", "bold");
      } else if (type === "semi-bold" || weight === 600) {
        doc.setFont("SplineSans-SemiBold", "normal");
      } else if (type === "medium" || weight === 500) {
        doc.setFont("SplineSans-Medium", "normal");
      } else {
        //type=regular
        //weight=400
        doc.setFont("SplineSans-Regular", "normal");
      }
    };

    const fontSize = size => {
      doc.setFontSize(size * (4 / 3)); //1pt = 1.2px //multiplication done to match the design
      lineHeight = Math.floor(size / 2);
    };

    const fontColor = (r, g, b, a) => {
      doc.setTextColor(r, g, b, a);
    };

    const horizLine = () => {
      doc.line(0, currentY, pageWidth, currentY);
    };

    const nextLine = numberOfLines =>
      (currentY += numberOfLines * lineHeight + 1);

    const trimString = (str, maxLen) => {
      let separator = " ";
      if (str.length <= maxLen) return str;
      return str.substr(0, str.lastIndexOf(separator, maxLen));
    };

    const trimText = (value, maxLength, maxLines) => {
      return doc.splitTextToSize(value, maxLength).slice(0, maxLines);
    };

    const getImageInfo = (image, width, height, { fit } = {}) => {
      height = height ?? width;
      width = width ?? height;
      let imageInfo, imageHeight, imageWidth, imageAspectRatio;

      if (fit != null && image != null) {
        imageInfo = doc.getImageProperties(image);
        imageHeight = imageInfo?.height ?? height;
        imageWidth = imageInfo?.width ?? width;
        imageAspectRatio = imageHeight / imageWidth;

        if (fit === "WIDTH" && width != null) {
          height = width * imageAspectRatio;
        } else if (fit === "HEIGHT" && height != null) {
          width = height / imageAspectRatio;
        }
      }

      return {
        width,
        height,
        widthOg: imageWidth,
        heightOg: imageHeight,
        aspectRatio: imageAspectRatio,
        fileType: imageInfo?.fileType?.toLowerCase() ?? "jpeg",
      };
    };

    const addImage = (image, x, y, width, height, { fit } = {}) => {
      const imageInfo = getImageInfo(image, width, height, { fit });

      if (image != null) {
        doc.addImage(
          image,
          imageInfo.fileType,
          x,
          y,
          imageInfo.width,
          imageInfo.height
        );
      }

      return { x, y, ...imageInfo };
    };

    const optimizeFontSizeWithRespectToMaxLines = (
      text,
      { maxWidth, maxLines = 1, fontSizeMax, fontSizeMin } = {}
    ) => {
      const splitText = () => {
        fontSize(fontSizeMax);

        const split = trimText(
          text,
          maxWidth,
          fontSizeMax === fontSizeMin ? maxLines : undefined
        );

        if (split.length > maxLines) {
          fontSizeMax--;
          return splitText();
        }

        // console.log("fontSizeMax:", fontSizeMax);
        // console.log("split:", split.join("").length);
        return split;
      };

      return splitText();
    };
    //endregion

    //region subscriber background & other info
    const { cardBackground, subscriberContact } =
      EnvSubscriberHelper.getEnvProfileCardRelatedValues();
    //endregion

    const addPageContent = employeeProfile => {
      //region process data from trainingCertificate
      //region front side data
      const employeeAvatar =
        getImage(employeeProfile?.user?.avatar, {
          params: `?r=${Math.random()}`,
        }) ?? "";

      let employeeName = employeeProfile?.user?.firstName?.trim() ?? "";
      const lastName = employeeProfile?.user?.lastName?.trim() ?? "";
      if (lastName?.length) employeeName += ` ${lastName}`;

      const employeeDesignation = employeeProfile?.designation?.trim() ?? "";
      const employeeDepartment = employeeProfile?.department?.trim() ?? "";

      const employeeCompanyName = getLocalisedValue(
        employeeProfile?.organization?.name,
        { preferredLocale: "en" }
      );

      //TODO: employeeUID
      const employeeUID = (
        employeeProfile?.user?.civilIdOrPassport ??
        employeeProfile?.employeeNo ??
        ""
      ).trim();

      const employeeCompanyLogo =
        getImage(employeeProfile?.organization?.logo, {
          params: `?r=${Math.random()}`,
        }) ?? "";

      const subscriberLogo =
        getImage(subscriberInfo?.user?.avatar, {
          params: `?r=${Math.random()}`,
        }) ?? "";
      //endregion

      //region back side data
      const completedTrainings = employeeProfile?.trainingCertificates
        ?.slice(0, 3) //6, 9
        ?.map(x => {
          const trainingDuration = x.trainingDuration ?? 1;
          const trainingCompletedAt = x.completedAt
            ? moment(parseInt(x.completedAt))
            : null;
          let trainingDate = "";

          if (trainingDuration > 1 && trainingCompletedAt) {
            const trainingStartedAt = trainingCompletedAt
              .clone()
              .subtract({ days: trainingDuration - 1 });

            if (
              trainingStartedAt.year() === trainingCompletedAt.year() &&
              trainingStartedAt.month() === trainingCompletedAt.month()
            ) {
              trainingDate = `${trainingStartedAt.format(
                "DD"
              )} - ${trainingCompletedAt.format(
                "DD"
              )} ${trainingCompletedAt.format("MMMM YYYY")}`;
            } else {
              trainingDate = `${trainingStartedAt.format(
                "DD MMMM YYYY"
              )} - ${trainingCompletedAt.format("DD MMMM YYYY")}`;
            }
          } else if (trainingCompletedAt) {
            trainingDate = trainingCompletedAt.format("DD MMMM YYYY");
          }

          return {
            title: cleanStringValue(
              getLocalisedValue(x?.trainingTitle, {
                preferredLocale: "en",
              })
            ),
            period: trainingDate ? `Course Date: ${trainingDate}` : "",
            expiresOn: x?.expiresAt
              ? `Expires on: ${moment(parseInt(x?.expiresAt)).format(
                  "DD MMMM YYYY"
                )}`
              : "",
            certificateNo: x?.certificateNumber
              ? `Certificate No.# ${x?.certificateNumber}`
              : "",
          };
        });

      const domainUrl = `${process.env.VUE_APP_EMPLOYEE_URL}/${
        i18n.locale || "en"
      }`;
      const shareUrl = `${domainUrl}/public-profile/${employeeProfile?.user?._id}`;
      const employeeQrCodeSize = 110;
      const employeeQrCode = `https://image-charts.com/chart?cht=qr&choe=UTF-8&chof=.png&icqrf=000000&icqrb=FF000000&chs=${employeeQrCodeSize}x${employeeQrCodeSize}&chl=${shareUrl}`;
      //endregion
      //endregion

      const data = {
        // front side
        employeeAvatar,
        employeeName: cleanStringValue(employeeName),
        employeeDesignationShort: cleanStringValue(employeeDesignation),

        employeeNameTitle: "Name",
        employeeCompanyNameTitle: "Company",
        employeeDesignationTitle: "Designation",
        employeeDepartmentTitle: "Department",
        employeeNoTitle: "Emp No",

        employeeCompanyName: cleanStringValue(employeeCompanyName),
        employeeDesignation: cleanStringValue(employeeDesignation),
        employeeDepartment: cleanStringValue(employeeDepartment),
        employeeUID: cleanStringValue(employeeUID),
        employeeCompanyLogo,
        subscriberLogo,

        // back side
        completedTrainings,
        employeeQrCode,
        scanMe: "Scan me to view more",
      };

      currentY = 2 * lineHeight;
      fontColor(0, 0, 0); //main color
      fontType("bold");

      //region front side
      //region background image
      addImage(cardBackground, 0, 0, pageWidth, pageHeight);
      //endregion

      //region employee avatar
      if (data.employeeAvatar?.length) {
        const { width, height } = getImageInfo(data.employeeAvatar, 182, 182, {
          fit: "HEIGHT",
        });

        doc.addImage(
          data.employeeAvatar,
          "jpeg",
          (gridOneWidth - width) / 2,
          currentY + 70,
          width,
          height
        );
      }
      //endregion

      //region vertical divider
      // fontColor(0, 0, 1);
      nextLine(4);
      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(gridOneWidth, 50, gridOneWidth, pageHeight - 50);
      //endregion

      //region employee info
      const maxInfoWidth = gridOneWidth - 205 - 35;

      //region employee name and designation short
      const employeeNameMaxWidth = gridOneWidth - 70;
      // fontType("bold");
      // fontColor(0, 0, 1);
      nextLine(45);

      const employeeNameSplit = optimizeFontSizeWithRespectToMaxLines(
        data.employeeName,
        {
          maxWidth: employeeNameMaxWidth,
          maxLines: 2,
          fontSizeMax: 34,
          fontSizeMin: 30,
        }
      );

      doc.text(employeeNameSplit, gridOneWidth / 2, currentY, {
        align: "center",
      });

      // fontType("medium");
      // fontColor(0, 0, 0); //designation short color

      fontSize(20);
      nextLine(employeeNameSplit.length < 2 ? 3 : 6.5);
      // INFO: For testing line length
      // doc.line(30, currentY, 30 + maxNameWidth, currentY);
      const employeeDesignationSplit = optimizeFontSizeWithRespectToMaxLines(
        data.employeeDesignationShort,
        {
          maxWidth: employeeNameMaxWidth,
          maxLines: 2,
          fontSizeMax: 20,
          fontSizeMin: 16,
        }
      );

      doc.text(employeeDesignationSplit, gridOneWidth / 2, currentY, {
        align: "center",
      });
      //endregion

      //region employee info
      fontSize(20);
      nextLine(employeeDesignationSplit.length < 2 ? 5 : 5.5);

      const infoValFontSize = 24;

      //region employeeCompanyName
      // fontColor(0, 0, 0); //info title color
      fontSize(25);
      // fontType("semi-bold");
      doc.text(data.employeeCompanyNameTitle, gridOneStart + 30, currentY);
      // fontColor(26, 26, 26); //info body color
      doc.text(":", gridOneStart + 180, currentY);
      // fontSize(infoValFontSize);
      // fontType("medium");
      // const companyNameTrimText = trimText(
      //   data.employeeCompanyName,
      //   maxInfoWidth,
      //   2
      // );
      const companyNameTrimText = optimizeFontSizeWithRespectToMaxLines(
        data.employeeCompanyName,
        {
          maxWidth: maxInfoWidth,
          maxLines: 2,
          fontSizeMax: 25,
          fontSizeMin: 20,
        }
      );
      doc.text(companyNameTrimText, gridOneStart + 200, currentY);
      //endregion

      //region employeeUID
      nextLine(companyNameTrimText.length < 2 ? 4 : 5);
      // fontColor(0, 0, 0); //info title color
      fontSize(25);
      // fontType("semi-bold");
      doc.text(data.employeeNoTitle, gridOneStart + 30, currentY);
      // fontColor(26, 26, 26); //info body color
      doc.text(":", gridOneStart + 180, currentY);
      // fontSize(infoValFontSize);
      // fontType("medium");
      // const employeeUIDTrimText = trimText(data.employeeUID, maxInfoWidth, 2);
      const employeeUIDTrimText = optimizeFontSizeWithRespectToMaxLines(
        data.employeeUID,
        {
          maxWidth: maxInfoWidth,
          maxLines: 2,
          fontSizeMax: 25,
          fontSizeMin: 20,
        }
      );
      doc.text(employeeUIDTrimText, gridOneStart + 200, currentY);
      //endregion

      //region employeeDepartment
      if (data.employeeDepartment.length && data.employeeDepartment !== ".") {
        nextLine(employeeUIDTrimText.length < 2 ? 4 : 5);
        // fontColor(0, 0, 0); //info title color
        fontSize(25);
        // fontType("semi-bold");
        doc.text(data.employeeDepartmentTitle, gridOneStart + 30, currentY);
        // fontColor(26, 26, 26); //info body color
        doc.text(":", gridOneStart + 180, currentY);
        // fontSize(infoValFontSize);
        // fontType("medium");
        // const departmentInfoTrimText = trimText(
        //   data.employeeDepartment,
        //   maxInfoWidth,
        //   2
        // );
        const departmentInfoTrimText = optimizeFontSizeWithRespectToMaxLines(
          data.employeeDepartment,
          {
            maxWidth: maxInfoWidth,
            maxLines: 2,
            fontSizeMax: 25,
            fontSizeMin: 20,
          }
        );
        doc.text(departmentInfoTrimText, gridOneStart + 200, currentY);
      }
      //endregion
      //endregion

      //region subscriberContact
      fontSize(18);
      doc.text(subscriberContact, pageWidth / 2, pageHeight - 12, {
        align: "center",
      });
      //endregion

      //region subscriber logo
      if (data.subscriberLogo?.length) {
        const { width, height } = getImageInfo(data.subscriberLogo, 75, 75, {
          fit: "HEIGHT",
        });

        addImage(
          data.subscriberLogo,
          pageWidth - width - 30,
          pageHeight - height - 25,
          width,
          height
        );
      }
      //endregion

      //region employee qrcode
      doc.addImage(
        data.employeeQrCode,
        "png",
        pageWidth - employeeQrCodeSize - 20,
        20,
        employeeQrCodeSize,
        employeeQrCodeSize
      );
      // fontSize(4);
      // fontColor(0, 0, 1); //expire color
      // doc.text(
      //   data.scanMe,
      //   pageWidth - employeeQrCodeSize / 2 - 30,
      //   employeeQrCodeSize + 30,
      //   {
      //     align: "center",
      //   }
      // );
      //endregion

      //region completed trainings
      fontSize(30);
      // fontType("bold");
      currentY = 1.5 * lineHeight;
      nextLine(4.3);

      if (data.completedTrainings.length) {
        doc.text("Successfully completed", gridTwoStart + 35, currentY);
        nextLine(4);
      }

      const maxTrainingTitleWidth = gridTwoWidth - 40 - 40;
      data.completedTrainings?.map((completedTraining, index) => {
        // if (index != 3) {
        //   completedTraining.title =
        //     "Health and Safety for Directors and Senior Managers";
        // }
        //
        // if (index == 3) {
        //   completedTraining.title = "Health and Safety for Directors";
        // }
        // completedTraining.title = completedTraining.title.toUpperCase();

        const trainingTitle = optimizeFontSizeWithRespectToMaxLines(
          completedTraining.title,
          {
            maxWidth: maxTrainingTitleWidth,
            maxLines: 2,
            fontSizeMax: 25,
            fontSizeMin: 20,
          }
        );
        doc.text(trainingTitle, gridTwoStart + 35, currentY);

        nextLine(trainingTitle.length < 2 ? 2 : 4.5);
        // fontType("medium");
        fontSize(19);
        // fontColor(0, 0, 1); //expire color
        doc.text(completedTraining.certificateNo, gridTwoStart + 35, currentY);

        if (completedTraining.period?.length) {
          nextLine(2.5);
          doc.text(completedTraining.period, gridTwoStart + 35, currentY);
        }

        nextLine(2.5);
        doc.text(completedTraining.expiresOn, gridTwoStart + 35, currentY);

        if (data.completedTrainings.length > index + 1) {
          nextLine(trainingTitle.length < 2 ? 5 : 5);
        }
      });
      //endregion
    };

    employeeProfiles.forEach((employeeProfile, index) => {
      addPageContent(employeeProfile);

      if (employeeProfiles.length > index + 1) {
        doc.addPage();
        lineHeight = Math.floor(normalFontSize / 2);
        currentY = lineHeight;
      }
    });

    if (debug) {
      window.open(doc.output("bloburl"), "_blank");
      return true;
    } else {
      if (download) {
        if (employeeProfiles.length === 1) {
          return doc.save(`${generateFilename(employeeProfiles[0])}.pdf`);
        } else {
          return doc.save(`profile-cards-${Date.now()}`);
        }
      } else {
        return doc.output(output ?? "blob");
      }
    }
  } catch (e) {
    console.log(
      "profileCardHelper.generateProfileCardPdf.exception:",
      e.message
    );

    return false;
  }
};

export const bulkDownloadProfileCards = async ({
  employeeProfiles,
  subscriberInfo,
  zip = false,
  debug = false,
  image = false,
}) => {
  try {
    if (!employeeProfiles?.length) return;

    await wait(100);

    if (!debug && (zip || image)) {
      const zip = new JSZip();
      const profileCards = zip.folder(`profiles-cards-${Date.now()}`);

      for (let index = 0; index < employeeProfiles.length; index++) {
        const employeeProfile = employeeProfiles[index];

        let blobData = generateProfileCardPdf({
          employeeProfiles: [employeeProfile],
          subscriberInfo,
          output: image ? "arraybuffer" : "blob",
        });

        if (blobData) {
          let fileName = generateFilename(employeeProfile, index);

          if (image) {
            blobData = await generateImageBlob(blobData);
            fileName += ".jpeg";
          } else {
            fileName += ".pdf";
          }

          if (employeeProfiles.length === 1) {
            downloadFile({
              data: blobData,
              fileName,
            });

            return true;
          }

          profileCards.file(fileName, blobData);
        }
      }

      if (profileCards?.files && Object.keys(profileCards?.files).length) {
        return await zip
          .generateAsync({ type: "blob" })
          .then(data => {
            downloadFile({ data, fileName: `profile-cards-${Date.now()}.zip` });
            return true;
          })
          .catch(e => {
            console.log(
              "profileCardHelper.bulkDownloadProfileCards:zip.generateAsync:error:",
              e.message
            );

            return false;
          });
      }
    } else {
      return !!generateProfileCardPdf({
        employeeProfiles,
        subscriberInfo,
        download: true,
        debug,
      });
    }
  } catch (e) {
    console.log(
      "profileCardHelper.bulkDownloadProfileCards.exception:",
      e.message
    );

    return false;
  }
};

const generateImageBlob = async arraybuffer => {
  try {
    const pdfDocument = await pdfjs.getDocument(arraybuffer).promise;
    const page = await pdfDocument.getPage(1);

    // holds viewport properties where page will be rendered
    let canvas = document.querySelector("#profileCardCanvas");
    if (!canvas) {
      canvas = document.createElement("canvas");
      canvas.id = "profileCardCanvas";
      canvas.style.display = "none";
      document.body.appendChild(canvas);
    }

    const renderContext = {
      canvasContext: canvas.getContext("2d"),
      viewport: page.getViewport({ scale: 2 }),
    };
    canvas.width = renderContext.viewport.width;
    canvas.height = renderContext.viewport.height;

    // wait for the page to render
    await page.render(renderContext).promise;

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          resolve(blob);
        },
        "image/jpeg",
        1
      );
    });
  } catch (e) {
    console.log("profileCardHelper.generateImageBlob.exception:", e.message);
    return null;
  }
};
